// Which API areas talk to the backend:
//   VITE_MOCK=1                 every area answers from the in-memory mock (dev, the clickable preview).
//   a real build                the areas in LIVE_AREAS go to the backend; the others answer from the mock,
//                               loaded lazily, and their screens say "demo data" (production while the backend
//                               grows). VITE_REAL_AREAS=a,b,… overrides the list, `*` means every area.
// An area here is a group of endpoints the backend ships together, finer than a mock module: progression's
// leagues, the market's trades or the account's push routes go live on their own.

export const API_AREAS = [
  "auth",
  "home",
  "account",
  "push",
  "admin",
  "captures",
  "cards",
  "collection",
  "replays",
  "destroy",
  "hoardex",
  "tierClaims",
  "profile",
  "showcaseEdit",
  "achievements",
  "progression",
  "chest",
  "freezes",
  "celebrations",
  "leagues",
  "feed",
  "wallet",
  "payments",
  "notifications",
  "settings",
  "market",
  "trades",
  "boosters",
  "podiums",
  "cosmetics",
  "battles",
  "pvp",
  "admin",
  "gifts",
  "shares",
  "safety",
  "growth",
] as const;
export type ApiArea = (typeof API_AREAS)[number];

export const MOCK_BUILD = import.meta.env.VITE_MOCK === "1";

/** What the backend serves today (build/foundations). Add an area here when its routes land. */
export const LIVE_AREAS: readonly ApiArea[] = [
  "auth",
  "home",
  "captures",
  "cards",
  "collection",
  "hoardex",
  "profile",
  "progression",
  "wallet",
  "notifications",
  "push",
  "boosters",
  "chest",
  "podiums",
  "admin",
  "showcaseEdit",
  "market",
  "trades",
  "destroy",
  "replays",
  "achievements",
  "tierClaims",
  "freezes",
  "celebrations",
  "leagues",
  "feed",
  "settings",
  "account",
  "cosmetics",
  "battles",
  "pvp",
  "gifts",
  "shares",
  "safety",
  "growth",
];

/** Parses `VITE_REAL_AREAS` (comma or space separated); unknown names are ignored, unset or empty means
 * LIVE_AREAS (an unset Docker build arg arrives empty) and `*` every area. */
export function parseRealAreas(value: string | undefined): Set<ApiArea> {
  if (value === undefined || value.trim() === "") return new Set(LIVE_AREAS);
  if (value.trim() === "*") return new Set(API_AREAS);
  const names = value.split(/[\s,]+/).filter(Boolean);
  return new Set(API_AREAS.filter((area) => names.includes(area)));
}

const real: ReadonlySet<ApiArea> = MOCK_BUILD ? new Set() : parseRealAreas(import.meta.env.VITE_REAL_AREAS);

/** Whether calls of this area go to the backend. */
export const isReal = (area: ApiArea) => real.has(area);

/** Whether a real build shows mock answers for this area: its screens must say so (`<DemoBadge>`). The
 * mock preview is a demo as a whole and says so elsewhere. */
export const isDemo = (area: ApiArea) => !MOCK_BUILD && !real.has(area);

/** Areas a real build answers from the mock. */
export const DEMO_AREAS: readonly ApiArea[] = API_AREAS.filter(isDemo);

/** The demo areas among `areas`, in order, once each. */
export function demoAreas(areas: readonly ApiArea[], demo: (area: ApiArea) => boolean = isDemo): ApiArea[] {
  return [...new Set(areas)].filter(demo);
}

/** Whether an action can be offered: the mock preview plays everything; a real build only what is live (a
 * demo answer must never fake an outcome on the player's real state). */
export const canUse = (area: ApiArea) => MOCK_BUILD || real.has(area);
